import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bookmark,
  Briefcase,
  Building2,
  Calendar,
  Eye,
  MapPin,
  Search,
  Send,
  SlidersHorizontal,
  User,
  Users,
} from 'lucide-react';
import { getApiBaseUrl } from '../../config/api';
import { htmlToExcerpt } from '../../utils/sanitizeHtml';
import './GuestJobsPanel.css';

const SAVED_KEY = 'cool_saved_job_ids';

function readSavedIds() {
  try {
    const raw = localStorage.getItem(SAVED_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

/**
 * Jobs board: hiring posts + interns looking for work.
 * Guests are prompted to log in for detail/apply; logged-in users go straight through.
 */
export default function GuestJobsPanel({ user = null, onNavigate }) {
  const API_URL = getApiBaseUrl();
  const navigate = useNavigate();
  const isLoggedIn = Boolean(user?.id);
  const [tab, setTab] = useState('hiring');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [filterOpen, setFilterOpen] = useState(false);
  const [savedIds, setSavedIds] = useState(readSavedIds);
  const [hiringPosts, setHiringPosts] = useState([]);
  const [lookingPosts, setLookingPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loginPrompt, setLoginPrompt] = useState(null);
  const filterRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [hiringRes, lookingRes] = await Promise.all([
        fetch(`${API_URL}/get_hiring_posts.php?limit=50`),
        fetch(`${API_URL}/get_looking_for_job_posts.php?limit=50`),
      ]);
      const hiringData = await hiringRes.json();
      const lookingData = await lookingRes.json();

      if (hiringData.success) setHiringPosts(hiringData.posts || []);
      if (lookingData.success) setLookingPosts(lookingData.posts || []);

      if (!hiringData.success && !lookingData.success) {
        setError('Unable to load job listings right now.');
      }
    } catch (err) {
      console.error('Guest jobs panel load error:', err);
      setError('Unable to load job listings right now.');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!filterOpen) return undefined;
    const onDocClick = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [filterOpen]);

  const promptLogin = (actionLabel) => {
    setLoginPrompt(actionLabel || 'continue');
  };

  const goToLogin = () => {
    window.location.href = '/login';
  };

  const goToPath = (path) => {
    onNavigate?.();
    navigate(path);
  };

  const handleHiringView = (postId) => {
    if (!isLoggedIn) {
      promptLogin('view job details');
      return;
    }
    goToPath(`/home/hiring/${postId}`);
  };

  const handleHiringApply = (postId) => {
    if (!isLoggedIn) {
      promptLogin('apply for this job');
      return;
    }
    goToPath(`/home/hiring/${postId}`);
  };

  const handleLookingView = (postId) => {
    if (!isLoggedIn) {
      promptLogin('view more details');
      return;
    }
    goToPath(`/home/looking-for-job/${postId}`);
  };

  const listingKey = (postId) => `${tab}:${postId}`;

  const toggleSaved = (postId) => {
    const id = listingKey(postId);
    setSavedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id];
      try {
        localStorage.setItem(SAVED_KEY, JSON.stringify(next));
      } catch {
        /* ignore quota / private mode */
      }
      return next;
    });
  };

  const posts = tab === 'hiring' ? hiringPosts : lookingPosts;
  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    return posts.filter((p) => {
      if (tab === 'hiring' && statusFilter !== 'all') {
        const closed = Boolean(p.hiring_closed) || p.hiring_accepting === false;
        if (statusFilter === 'open' && closed) return false;
        if (statusFilter === 'closed' && !closed) return false;
      }
      if (statusFilter === 'saved' && !savedIds.includes(`${tab}:${p.id}`)) return false;
      if (!q) return true;
      return listingSearchText(p, tab).includes(q);
    });
  }, [posts, q, tab, statusFilter, savedIds]);

  return (
    <div className="jobs-panel">
      <div className="jobs-panel-toolbar">
        <div className="jobs-panel-tabs" role="tablist" aria-label="Job listings">
          <TabButton
            active={tab === 'hiring'}
            onClick={() => setTab('hiring')}
            icon={Briefcase}
            label="Available job postings"
            count={hiringPosts.length}
          />
          <TabButton
            active={tab === 'looking'}
            onClick={() => setTab('looking')}
            icon={Users}
            label="Interns looking for jobs"
            count={lookingPosts.length}
          />
        </div>
        <div className="jobs-search-row" ref={filterRef}>
          <div className="jobs-search-wrap">
            <Search size={18} strokeWidth={2} aria-hidden="true" />
            <input
              type="search"
              className="jobs-panel-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                tab === 'hiring'
                  ? 'Search jobs by title, company, or location'
                  : 'Search interns by name, skills, or location'
              }
              aria-label="Search listings"
            />
            <button
              type="button"
              className={`jobs-filter-btn${filterOpen ? ' is-open' : ''}`}
              onClick={() => setFilterOpen((open) => !open)}
              aria-label="Filter listings"
              aria-expanded={filterOpen}
            >
              <SlidersHorizontal size={18} strokeWidth={2} />
            </button>
          </div>
          {filterOpen && (
            <div className="jobs-filter-menu" role="menu">
              <button
                type="button"
                role="menuitem"
                className={statusFilter === 'all' ? 'is-active' : ''}
                onClick={() => {
                  setStatusFilter('all');
                  setFilterOpen(false);
                }}
              >
                All listings
              </button>
              {tab === 'hiring' && (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    className={statusFilter === 'open' ? 'is-active' : ''}
                    onClick={() => {
                      setStatusFilter('open');
                      setFilterOpen(false);
                    }}
                  >
                    Open jobs only
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className={statusFilter === 'closed' ? 'is-active' : ''}
                    onClick={() => {
                      setStatusFilter('closed');
                      setFilterOpen(false);
                    }}
                  >
                    Closed jobs
                  </button>
                </>
              )}
              <button
                type="button"
                role="menuitem"
                className={statusFilter === 'saved' ? 'is-active' : ''}
                onClick={() => {
                  setStatusFilter('saved');
                  setFilterOpen(false);
                }}
              >
                Saved listings
              </button>
            </div>
          )}
        </div>
      </div>

      {!loading && !error && (
        <p className="jobs-panel-meta">
          {filtered.length} of {posts.length} {posts.length === 1 ? 'listing' : 'listings'}
          {q ? ` matching “${query.trim()}”` : ''}
        </p>
      )}

      <div className="jobs-panel-list-wrap">
        {loading ? (
          <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>Loading listings…</p>
        ) : error ? (
          <p style={{ margin: 0, color: '#b91c1c', fontSize: 14 }}>{error}</p>
        ) : posts.length === 0 ? (
          <div className="jobs-panel-empty">
            <p>
              {tab === 'hiring'
                ? 'No job postings available right now.'
                : 'No interns are currently looking for jobs.'}
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="jobs-panel-empty">
            <p>
              {statusFilter === 'saved'
                ? 'No saved listings yet. Use the bookmark on a card to save it.'
                : 'No listings match that search.'}
            </p>
          </div>
        ) : (
          <ul className="jobs-panel-list">
            {filtered.map((post) =>
              tab === 'hiring' ? (
                <HiringItem
                  key={post.id}
                  post={post}
                  saved={savedIds.includes(`hiring:${post.id}`)}
                  onToggleSave={() => toggleSaved(post.id)}
                  onView={() => handleHiringView(post.id)}
                  onApply={() => handleHiringApply(post.id)}
                />
              ) : (
                <LookingItem
                  key={post.id}
                  post={post}
                  saved={savedIds.includes(`looking:${post.id}`)}
                  onToggleSave={() => toggleSaved(post.id)}
                  onView={() => handleLookingView(post.id)}
                />
              )
            )}
          </ul>
        )}
      </div>

      {loginPrompt && !isLoggedIn && (
        <div
          className="jobs-login-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Login required"
          onClick={() => setLoginPrompt(null)}
        >
          <div className="jobs-login-card" onClick={(e) => e.stopPropagation()}>
            <h4>Log in to continue</h4>
            <p>Please log in first to {loginPrompt}.</p>
            <div className="jobs-login-actions">
              <button type="button" className="jobs-action jobs-action--primary" onClick={goToLogin}>
                Log in
              </button>
              <button
                type="button"
                className="jobs-action jobs-action--ghost"
                onClick={() => setLoginPrompt(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function listingSearchText(post, tab) {
  if (tab === 'hiring') {
    return [
      post.title,
      post.hiring_company,
      post.hiring_location,
      post.hiring_description,
      post.text,
      post.content,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }

  const skills = Array.isArray(post.looking_for_job_skills)
    ? post.looking_for_job_skills.join(' ')
    : '';
  return [
    post.title,
    post.first_name,
    post.last_name,
    post.username,
    post.looking_for_job_location,
    post.looking_for_job_availability,
    post.looking_for_job_description,
    post.text,
    post.content,
    skills,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function TabButton({ active, onClick, icon: Icon, label, count }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={`jobs-tab${active ? ' is-active' : ''}`}
      onClick={onClick}
    >
      <span className="jobs-tab-icon">
        <Icon size={18} strokeWidth={2} />
      </span>
      <span className="jobs-tab-copy">
        <span className="jobs-tab-label">{label}</span>
        <span className="jobs-tab-count">
          {count} {count === 1 ? 'listing' : 'listings'}
        </span>
      </span>
    </button>
  );
}

function SaveButton({ saved, onClick }) {
  return (
    <button
      type="button"
      className={`jobs-save-btn${saved ? ' is-saved' : ''}`}
      onClick={onClick}
      aria-label={saved ? 'Remove from saved listings' : 'Save listing'}
      title={saved ? 'Saved' : 'Save'}
    >
      <Bookmark size={18} strokeWidth={2} fill={saved ? 'currentColor' : 'none'} />
    </button>
  );
}

function HiringItem({ post, saved, onToggleSave, onView, onApply }) {
  const description = post.hiring_description || post.text || post.content || '';
  const excerpt = htmlToExcerpt(description, 140);
  const company = post.hiring_company || '';
  const location = post.hiring_location || '';
  const deadline = post.hiring_deadline || '';
  const closed = Boolean(post.hiring_closed) || post.hiring_accepting === false;

  return (
    <li className="jobs-card">
      <div className="jobs-card-top">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          <span className="jobs-badge jobs-badge--hiring">Hiring</span>
          {closed && <span className="jobs-badge jobs-badge--closed">Closed</span>}
        </div>
        <SaveButton saved={saved} onClick={onToggleSave} />
      </div>
      <h3 className="jobs-card-title">{post.title}</h3>
      <div className="jobs-card-meta">
        {company ? (
          <p className="jobs-card-meta-row">
            <Building2 size={14} strokeWidth={2} />
            <span>{company}</span>
          </p>
        ) : null}
        {location ? (
          <p className="jobs-card-meta-row">
            <MapPin size={14} strokeWidth={2} />
            <span>{location}</span>
          </p>
        ) : null}
        <p className="jobs-card-meta-row">
          <Calendar size={14} strokeWidth={2} />
          <span>
            {closed
              ? 'Applications closed'
              : deadline
                ? `Apply by ${deadline}`
                : 'Open until closed by employer'}
          </span>
        </p>
      </div>
      {excerpt ? <p className="jobs-card-excerpt">{excerpt}</p> : null}
      <div className="jobs-card-actions">
        <button type="button" className="jobs-action jobs-action--ghost" onClick={onView}>
          <Eye size={15} strokeWidth={2} />
          View details
        </button>
        {!closed && (
          <button type="button" className="jobs-action jobs-action--primary" onClick={onApply}>
            <Send size={15} strokeWidth={2} />
            Apply
          </button>
        )}
      </div>
    </li>
  );
}

function LookingItem({ post, saved, onToggleSave, onView }) {
  const description =
    post.looking_for_job_description || post.text || post.content || '';
  const excerpt = htmlToExcerpt(description, 140);
  const location = post.looking_for_job_location || '';
  const availability = post.looking_for_job_availability || '';
  const skills = Array.isArray(post.looking_for_job_skills)
    ? post.looking_for_job_skills
    : [];
  const name =
    [post.first_name, post.last_name].filter(Boolean).join(' ') ||
    post.username ||
    'Intern';

  return (
    <li className="jobs-card">
      <div className="jobs-card-top">
        <span className="jobs-badge jobs-badge--looking">Looking for job</span>
        <SaveButton saved={saved} onClick={onToggleSave} />
      </div>
      <h3 className="jobs-card-title">{post.title}</h3>
      <div className="jobs-card-meta">
        <p className="jobs-card-meta-row">
          <User size={14} strokeWidth={2} />
          <span>{name}</span>
        </p>
        {location ? (
          <p className="jobs-card-meta-row">
            <MapPin size={14} strokeWidth={2} />
            <span>{location}</span>
          </p>
        ) : null}
        {availability ? (
          <p className="jobs-card-meta-row">
            <Calendar size={14} strokeWidth={2} />
            <span>{availability}</span>
          </p>
        ) : null}
        {skills.length > 0 ? (
          <p className="jobs-card-meta-row">
            <Briefcase size={14} strokeWidth={2} />
            <span>
              {skills.slice(0, 5).join(' · ')}
              {skills.length > 5 ? '…' : ''}
            </span>
          </p>
        ) : null}
      </div>
      {excerpt ? <p className="jobs-card-excerpt">{excerpt}</p> : null}
      <div className="jobs-card-actions">
        <button type="button" className="jobs-action jobs-action--ghost" onClick={onView}>
          <Eye size={15} strokeWidth={2} />
          View details
        </button>
      </div>
    </li>
  );
}
